import { BaseAPICallsHandler } from "../BaseAPICallsHandler";
import { NetworkUtils } from "../NetworkUtils";
import { ServerConfig } from "../ServerConfig";
import { Order } from "../../../Models/Order";
import { Delivery } from "../../../Models/Delivery";

export class OrderAPIHandler {
  public static shared = new OrderAPIHandler();

  apiHandler = BaseAPICallsHandler.shared;
  serverConfig = ServerConfig.shared;
  networkUtils = NetworkUtils.shared;

  constructor();
  constructor(
    apiHandler: BaseAPICallsHandler,
    serverConfig: ServerConfig,
    networkUtils: NetworkUtils
  );
  public constructor(...params: any[]) {
    if (params.length >= 3) {
      this.apiHandler = params[0];
      this.serverConfig = params[1];
      this.networkUtils = params[2];
    }
  }

  // The paynent instance here is the razorpay payment instance
  public async placeOrder(
    actionCardId: string,
    promoCode: string | undefined = undefined
  ): Promise<{ order: Order; paymentInstance: any }> {
    const url = this.serverConfig.orderAPIs.placeOrderAPI;
    const authHeaders = this.networkUtils.addAuthHeaders({});

    try {
      const response = await this.apiHandler.makePostRequest(authHeaders, url, {
        actionCardId,
        promoCode,
      });
      const placedOrder = response.data.placedOrder;
      const paymentInstance = response.data.paymentInstance;
      const order = new Order(placedOrder);
      return Promise.resolve({ order, paymentInstance });
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public async approveOrder(
    orderId: string
  ): Promise<{ order: Order; delivery: Delivery }> {
    const url = this.serverConfig.orderAPIs.approveOrderAPI(orderId);
    const authHeaders = this.networkUtils.addAuthHeaders({});

    try {
      const response = await this.apiHandler.makePostRequest(
        authHeaders,
        url,
        {}
      );
      const orderData = response.data.order;
      const deliveryData = response.data.delivery;
      const order = new Order(orderData);
      const delivery = new Delivery(deliveryData);
      return Promise.resolve({ order, delivery });
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public async generateTaxInvoiceForSellerAPI(orderId: string): Promise<Blob> {
    const url =
      this.serverConfig.orderAPIs.generateTaxInvoiceForSellerAPI(orderId);
    const headers = this.networkUtils.addAuthHeaders({
      Accept: "application/pdf",
    });
    try { const response = await fetch(url, {
        method: "GET",
        headers: headers,
      });
      if (!response.ok) {
        throw new Error(
          `Failed to generate tax invoice PDF. Status: ${response.status}`
        );
      }
      const blob = await response.blob(); // PDF comes as blob
      return Promise.resolve(blob);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public async generateTaxInvoiceForBuyerAPI(orderId: string): Promise<Blob> {
    const url =
      this.serverConfig.orderAPIs.generateTaxInvoiceForBuyerAPI(orderId);
    const headers = this.networkUtils.addAuthHeaders({
      Accept: "application/pdf",
    });
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: headers,
      });
      if (!response.ok) {
        throw new Error(
          `Failed to generate tax invoice PDF. Status: ${response.status}`
        );
      }
      const blob = await response.blob(); // PDF comes as blob
      return Promise.resolve(blob);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public async downloadOrderReceiptPdfForBuyer(orderId: string): Promise<Blob> {
    const url = this.serverConfig.orderAPIs.generateOrderRecieptPdfForBuyerAPI(orderId);
    const headers = this.networkUtils.addAuthHeaders({
      Accept: "application/pdf",
    });

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to download order receipt PDF. Status: ${response.status}`
        );
      }

      const blob = await response.blob(); // PDF comes as blob
      return Promise.resolve(blob);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public async downloadOrderReceiptPdfForSeller(orderId: string): Promise<Blob> {
    const url = this.serverConfig.orderAPIs.generateOrderRecieptPdfForSellerAPI(orderId);
    const headers = this.networkUtils.addAuthHeaders({
      Accept: "application/pdf",
    });
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: headers,
      });
      if (!response.ok) {
        throw new Error(
          `Failed to download order receipt PDF for freelancer. Status: ${response.status}`
        );
      }
      const blob = await response.blob(); // PDF comes as blob
      return Promise.resolve(blob);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public async verifyPlacedOrder(
    orderId: string,
    paymentId: string,
    signature: string,
    ggOrderId: string
  ): Promise<{ paymentSuccess: boolean; order: Order }> {
    const url = this.serverConfig.orderAPIs.verifyPlacedOrderAPI(ggOrderId);
    const authHeaders = this.networkUtils.addAuthHeaders({});

    try {
      const response = await this.apiHandler.makePostRequest(authHeaders, url, {
        orderId,
        paymentId,
        signature,
      });
      const paymentSuccess = response.data.paymentSuccess;
      const updatedOrder = response.data.updatedOrder;
      const order = new Order(updatedOrder);
      return Promise.resolve({ paymentSuccess, order });
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public async getOrdersByBuyerId(): Promise<Order[]> {
    const url = this.serverConfig.orderAPIs.getOrderByBuyerIDAPI;
    const authHeaders = this.networkUtils.addAuthHeaders({});

    try {
      const response = await this.apiHandler.makeGetRequest(authHeaders, url);
      const data = response.data.orders.map(
        (orderJson: any) => new Order(orderJson)
      );
      return Promise.resolve(data);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public async getOrdersBySellerId(): Promise<Order[]> {
    const url = this.serverConfig.orderAPIs.getOrderBySellerIDAPI;
    const authHeaders = this.networkUtils.addAuthHeaders({});

    try {
      const response = await this.apiHandler.makeGetRequest(authHeaders, url);
      const data = response.data.orders.map(
        (orderJson: any) => new Order(orderJson)
      );
      return Promise.resolve(data);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public async markAllRevisionsAsRead(orderId: string): Promise<Order> {
    const url = this.serverConfig.orderAPIs.markAllRevisionsAsReadAPI(orderId);
    const authHeaders = this.networkUtils.addAuthHeaders({});

    try {
      const response = await this.apiHandler.makePutRequest(
        authHeaders,
        url,
        {}
      );
      const orderData = response.data.order;
      const order = new Order(orderData);
      return Promise.resolve(order);
    } catch (err) {
      return Promise.reject(err);
    }
  }
}
